/**
 * /settings/assistant — the assistant's tenant-editable identity: what it
 * calls itself, a factual paragraph about the organization, and the timezone
 * its "today" is computed in. Every field is optional; a cleared field falls
 * back to the platform default. Behavior rules do NOT belong here — the page
 * says so and points at Safety & Guardrails.
 */
import { useState, type FormEvent } from 'react';
import { Banner, Button, Input, Select, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import type { AssistantProfile } from '../../lib/types.js';
import { useAssistantProfileLogic, useSaveAssistantProfileLogic } from './useAssistantLogic.js';

const SUBTITLE =
  'How the assistant introduces itself and understands your organization. It mirrors your ' +
  'team’s own terminology from conversations and your data — no term lists to maintain. ' +
  'Behavior rules live in Safety & Guardrails, not here.';

/**
 * Curated US zones for now (Travis, 2026-08-03) — the full IANA list
 * (Intl.supportedValuesOf) made the panel unwieldy and the DS Select has no
 * flip-above collision handling yet (see DESIGN-SYSTEM-CANDIDATES.md). The
 * API accepts any IANA zone, so widening later is a list change here only.
 */
const TIMEZONES: { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern — America/New_York' },
  { value: 'America/Chicago', label: 'Central — America/Chicago' },
  { value: 'America/Denver', label: 'Mountain — America/Denver' },
  { value: 'America/Phoenix', label: 'Arizona — America/Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific — America/Los_Angeles' },
  { value: 'America/Anchorage', label: 'Alaska — America/Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii — Pacific/Honolulu' },
];

const ProfileForm = ({ tenantId, profile }: { tenantId: string; profile: AssistantProfile }) => {
  const saveLogic = useSaveAssistantProfileLogic(tenantId);
  const [assistantName, setAssistantName] = useState(profile.assistantName ?? '');
  const [orgDescription, setOrgDescription] = useState(profile.orgDescription ?? '');
  const [timezone, setTimezone] = useState(profile.timezone ?? '');

  const edit =
    <T,>(setter: (v: T) => void) =>
    (value: T) => {
      saveLogic.clearSaved();
      setter(value);
    };

  const overNameLimit = assistantName.trim().length > profile.maxAssistantNameChars;
  const overDescLimit = orgDescription.trim().length > profile.maxOrgDescriptionChars;
  const canSubmit = !saveLogic.isSubmitting && !overNameLimit && !overDescLimit;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      await saveLogic.save({
        assistantName: assistantName.trim() || null,
        orgDescription: orgDescription.trim() || null,
        timezone: timezone || null,
      });
    } catch {
      // Surfaced via saveLogic.error.
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="border-outline bg-surface-elevated max-w-[780px] space-y-4 rounded-xl border p-5 shadow-sm"
    >
      {saveLogic.error && (
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>{saveLogic.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      <div>
        <label htmlFor="assistant-name" className="text-ink mb-1.5 block text-xs font-semibold">
          Assistant name
        </label>
        <Input
          id="assistant-name"
          type="text"
          value={assistantName}
          onChange={(e) => edit(setAssistantName)(e.target.value)}
          disabled={saveLogic.isSubmitting}
          placeholder="e.g., Ricky"
          autoComplete="off"
          className="max-w-[340px]"
        />
        <p className="text-ink-subtle mt-1.5 text-xs leading-snug">
          What the assistant calls itself — its message-sender identity and how it introduces
          itself. Leave blank for the platform default.
        </p>
      </div>

      <div>
        <label htmlFor="org-description" className="text-ink mb-1.5 block text-xs font-semibold">
          About your organization
        </label>
        <textarea
          id="org-description"
          value={orgDescription}
          onChange={(e) => edit(setOrgDescription)(e.target.value)}
          disabled={saveLogic.isSubmitting}
          rows={5}
          className="border-outline-strong bg-surface text-ink focus-visible:ring-primary w-full rounded-lg border p-3 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2"
          placeholder="A few factual sentences: what you do, what you service, what you call things."
        />
        <p
          className={`mt-1 text-right text-xs tabular-nums ${overDescLimit ? 'text-danger font-semibold' : 'text-ink-subtle'}`}
        >
          {orgDescription.trim().length} / {profile.maxOrgDescriptionChars} characters
        </p>
        <p className="text-ink-subtle text-xs leading-snug">
          Context, not instructions — the assistant reads this as facts about who it works for.
        </p>
      </div>

      <div>
        <label htmlFor="assistant-timezone" className="text-ink mb-1.5 block text-xs font-semibold">
          Timezone
        </label>
        <Select
          id="assistant-timezone"
          options={TIMEZONES}
          value={timezone ? [timezone] : []}
          onChange={(v) => edit(setTimezone)(v[0] ?? '')}
          searchable={false}
          placeholder="Platform default (America/Chicago)"
          className="max-w-[340px]"
        />
        <p className="text-ink-subtle mt-1.5 text-xs leading-snug">
          The assistant computes &ldquo;today&rdquo; in this zone for schedules and due dates.
        </p>
      </div>

      <div className="flex items-center justify-end gap-3 pt-1">
        {saveLogic.saved && <p className="text-success text-sm font-semibold">Saved ✓</p>}
        <Button type="submit" disabled={!canSubmit}>
          {saveLogic.isSubmitting ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
};

export const AssistantPage = () => {
  const meQuery = useMe();
  const tenantId = meQuery.data?.tenant.id;
  const profileQuery = useAssistantProfileLogic(tenantId);

  return (
    <AppShell>
      <h2 className="text-ink mb-1.5 text-2xl font-bold">Assistant</h2>
      <p className="text-ink-muted mb-5 max-w-[72ch] text-sm">{SUBTITLE}</p>

      {profileQuery.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{profileQuery.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {meQuery.isLoading || !tenantId || profileQuery.isLoading ? (
        <Skeleton className="h-96 w-full max-w-[780px] rounded-xl" />
      ) : profileQuery.data ? (
        <ProfileForm key={tenantId} tenantId={tenantId} profile={profileQuery.data} />
      ) : null}
    </AppShell>
  );
};
